import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Flame, Share2 } from 'lucide-react';
import { getActiveSalon } from 'Containers/Auth/store/selectors';
import useIsPremiumUnlocked from 'utils/useIsPremiumUnlocked';
import { modalActions } from 'Containers/Modals/store';
import { toastActions } from 'Containers/Toasts/store';
import { ROUTES } from 'routes/constants';
import { PREMIUM_UPGRADE_PATH } from 'utils/premiumUpgradePath';
import useWindowSize from 'utils/useWindowSize';
import { ReviewStoryCard, useStoryShare } from 'Containers/Advertising/storyKit';
import styles from './ShareReviewStory.module.scss';

const ShareReviewStoryButton = ({ review }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobile } = useWindowSize();
  const salon = useSelector(getActiveSalon());
  const [open, setOpen] = useState(false);

  const isPremiumUnlocked = useIsPremiumUnlocked();

  const onToast = useCallback(
    (kind) => {
      if (kind === 'error') {
        dispatch(
          toastActions.OPEN_TOAST.SUCCESS({
            type: 'worker',
            props: {
              title: t('scheduler.workerSoldOutModal.shareError'),
              warning: true,
            },
          }),
        );
        return;
      }
      const title =
        kind === 'retry'
          ? t('scheduler.workerSoldOutModal.shareRetryHint')
          : kind === 'photos'
            ? t('scheduler.workerSoldOutModal.shareSaveToPhotosHint')
            : t('scheduler.workerSoldOutModal.shareSavedFallback');
      dispatch(
        toastActions.OPEN_TOAST.SUCCESS({
          type: 'worker',
          props: {
            title,
            warning: false,
          },
        }),
      );
    },
    [dispatch, t],
  );

  const { captureRef, ensureFile, share, sharing } = useStoryShare({
    fileBaseName: `zetabarber-review-${review?.id_recensione || 'x'}`,
    isMobile,
    onToast,
  });

  const goToUpgrade = () => {
    navigate(PREMIUM_UPGRADE_PATH);
  };

  const handleClick = () => {
    if (!isPremiumUnlocked) {
      goToUpgrade();
      return;
    }
    setOpen(true);
  };

  if (!review) return null;

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={
          isPremiumUnlocked ? styles.share_btn : styles.share_btn_locked
        }
        aria-label={
          isPremiumUnlocked
            ? t('reviews.shareStory')
            : t('reviews.shareStoryPremiumHint')
        }
      >
        {isPremiumUnlocked ? (
          <>
            <Share2 size={15} strokeWidth={2.25} aria-hidden />
            <span>{t('reviews.shareStory')}</span>
          </>
        ) : (
          <>
            <Flame size={15} strokeWidth={2.4} fill="currentColor" aria-hidden />
            <span>{t('reviews.shareStoryPremium')}</span>
          </>
        )}
      </button>

      {open ? (
        <div className={styles.backdrop} role="dialog" aria-modal="true">
          <div className={styles.modal}>
            <h3 className={styles.title}>{t('advertising.generator.title')}</h3>
            <div className={styles.preview}>
              <ReviewStoryCard captureRef={captureRef} salon={salon} review={review} t={t} />
            </div>
            <div className={styles.actions}>
              <button type="button" className={styles.secondary} onClick={() => setOpen(false)}>
                {t('advertising.actions.close')}
              </button>
              <button
                type="button"
                className={styles.primary}
                disabled={sharing}
                onPointerDown={() => ensureFile().catch(() => {})}
                onClick={async () => {
                  await share();
                  setOpen(false);
                }}
              >
                {sharing
                  ? t('scheduler.workerSoldOutModal.preparing')
                  : t('advertising.actions.share')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* keep capture mounted lightly when modal closed via open gate above */}
    </>
  );
};

export default ShareReviewStoryButton;
